import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection } from "rxdb-hooks";
import { SYNCABLE_META } from "./common";

export const RXDB_EPOCUS_BILLING_STATUS_COLLECTION_NAME =
  "epocus-billing-status";

export const useRxCollectionEpocusBillingStatus =
  (): RxCollection<EpocusBillingStatus> | null =>
    useRxCollection<EpocusBillingStatus>(
      RXDB_EPOCUS_BILLING_STATUS_COLLECTION_NAME,
    );

export const BILLING_STATES = [
  "DRAFT",
  "INCOMPLETE",
  "READY_TO_BILL",
  "NON_BILLABLE",
  "EXPORTED",
] as const;
export type BillingState = (typeof BILLING_STATES)[number];

export const BILLING_HOLD_REASONS = [
  "NON_DIAGNOSTIC",
  "MAJOR_DISCREPANCY",
] as const;
export type BillingHoldReason = (typeof BILLING_HOLD_REASONS)[number];

export const BILLING_MISSING_ITEMS = [
  "PATIENT_NOT_LINKED",
  "NO_DOB",
  "NO_MRN",
  "NO_DOS",
  "NO_NPI",
  "NO_CODES",
  "NO_NEEDLE_IMAGE",
] as const;
export type BillingMissingItem = (typeof BILLING_MISSING_ITEMS)[number];

const EPOCUS_BILLING_STATUS_SCHEMA_LITERAL = {
  title: "epocus billing status schema",
  version: 2,
  description:
    "Where a study stands for billing. Built by the server and pulled only; the device never writes it.",
  type: "object",
  primaryKey: "id",
  properties: {
    id: { type: "string", maxLength: 100 },
    examId: { type: "string", maxLength: 100 },
    encounterId: { type: "string", maxLength: 100 },
    programId: { type: ["string", "null"], maxLength: 100 },
    performerId: { type: ["string", "null"], maxLength: 100 },
    status: { type: "string", enum: BILLING_STATES },
    missingItems: {
      type: "array",
      items: { type: "string", enum: BILLING_MISSING_ITEMS },
    },
    holdReason: {
      type: ["string", "null"],
      enum: [...BILLING_HOLD_REASONS, null],
    },
    qaAfterExport: {
      type: ["string", "null"],
      enum: [...BILLING_HOLD_REASONS, null],
    },
    amendedAfterExportAt: { type: ["string", "null"], format: "date-time" },
    computedAt: { type: "string", format: "date-time" },
    examType: { type: ["string", "null"] },
    examSignedAt: { type: ["string", "null"], format: "date-time" },
    examCreatedAt: { type: ["string", "null"], format: "date-time" },
    meta: SYNCABLE_META,
  },
  required: [
    "id",
    "examId",
    "encounterId",
    "programId",
    "performerId",
    "status",
    "missingItems",
    "holdReason",
    "qaAfterExport",
    "amendedAfterExportAt",
    "computedAt",
    "examType",
    "examSignedAt",
    "examCreatedAt",
    "meta",
  ],
  indexes: ["examId"],
  /*
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;

const schemaTyped = toTypedRxJsonSchema(EPOCUS_BILLING_STATUS_SCHEMA_LITERAL);

export type EpocusBillingStatus = Omit<
  ExtractDocumentTypeFromTypedRxJsonSchema<typeof schemaTyped>,
  "holdReason" | "qaAfterExport"
> & {
  holdReason: BillingHoldReason | null;
  qaAfterExport: BillingHoldReason | null;
};

export const EPOCUS_BILLING_STATUS_SCHEMA: RxJsonSchema<EpocusBillingStatus> =
  EPOCUS_BILLING_STATUS_SCHEMA_LITERAL as unknown as RxJsonSchema<EpocusBillingStatus>;
